<script lang="ts">
    // Components
    import ChromePlayer from '~/components/structure/Players/components/ChromePlayer.svelte';
    import MiniPlayer from '~/components/structure/Players/components/MiniPlayer.svelte';
    import FullScreenPlayer from '~/components/structure/Players/FullScreenPlayer.svelte';
    import { onMount, onDestroy } from 'svelte';

    // Actions
    import {
        makePausePlaybackAction,
        makeResumePlaybackAction,
    } from '~/actions/playback-actions';

    // Utils
    import { get } from 'svelte/store';
    import { getPlayback } from '~/utils/playback';
    import { getJetPerform } from '~/jet';
    import { SPACEBAR, isInteractiveElement } from '~/utils/keyboard';

    // Shared Utils
    import { loggerFor } from '@amp/web-apps-logger';
    import { mediaQueries } from '@amp/web-app-components/src/stores/media-query';

    // Stores
    import { fullScreenPlayerState } from '~/stores/full-screen-player';
    import { getI18n } from '~/stores/i18n';
    import { queueMetadata } from '~/stores/queue-metadata';

    const i18n = getI18n();
    const log = loggerFor('<PlayerBar>');
    const perform = getJetPerform();
    const { state, queue } = getPlayback();

    let fullScreenPlayerTriggerElement: HTMLElement = null;
    let showFooterBar: boolean = false;

    // set chrome to visible on first render
    state.setIsChromeVisible(true);

    $: nowPlayingItem = $queue.nowPlayingItem;
    $: nowPlayingItemId = $queue.nowPlayingItemInfo?.id;
    $: item = !!nowPlayingItemId && $queueMetadata?.get(nowPlayingItemId);
    $: playbackType = nowPlayingItem?.item?.playbackType;

    $: mq = mediaQueries ? $mediaQueries : null;

    $: isPaused = $queue.derivedStates.isPaused;

    $: playOrPauseAction = isPaused
        ? makeResumePlaybackAction()
        : makePausePlaybackAction();

    function setFooterBarVisibility() {
        showFooterBar = mq === 'xsmall' && window.innerWidth < 484;
    }

    // Toggles the display of the fullscreen mobile Now Playing modal.
    function handleOpenFullScreenPlayer(e: CustomEvent) {
        fullScreenPlayerTriggerElement = e.target ?? e.detail.target;
        fullScreenPlayerState.setVisibility(true);
    }

    const handleCloseFullScreenPlayer = () => {
        fullScreenPlayerTriggerElement = null;
        fullScreenPlayerState.setVisibility(false);
    };

    const handleKeydown = (event: KeyboardEvent): void => {
        switch (event.key) {
            case SPACEBAR:
                // If an input element is focused, notably the search field,
                // we don't want to override its default spacebar behavior.
                if (isInteractiveElement(event.target as Element)) {
                    break;
                }

                event.preventDefault();

                if (!$queue.nowPlayingItem) {
                    break;
                }

                // TODO: Using the code below causes the player to pause
                //       for a second, but then resume playing.  Same happens
                //       on Music in Prod, so fix for both.
                //       rdar://140130104 (Onyx Music: Pressing spacebar to toggle playback can get in a state where playback resumes)
                const { isPlaying } = $queue.derivedStates;

                perform(playOrPauseAction);
                break;
        }
    };

    function handleNavigate(e: CustomEvent) {
        perform(e.detail.action);
    }

    onMount(() => {
        // set chrome to visible on re-mounts of this component
        state.setIsChromeVisible(true);
        setFooterBarVisibility();
    });

    onDestroy(() => state.setIsChromeVisible(false));
</script>

<svelte:window on:keydown={handleKeydown} on:resize={setFooterBarVisibility} />

<div
    class="player-bar player-bar__floating-player"
    data-testid="player-bar"
    aria-label={$i18n.t('ASE.Web.MusicClassical.LCD.Section')}
>
    <!--
        Hide the WCs on SSR. The cause a flash of unstyled slots before the
        WC code loads. We could consider actually SSRing these to avoid this
        problem, but that requires personalization, so best to hold off:
        rdar://97613131 (Serverside render MK WCs)
    -->
    {#if typeof window !== 'undefined'}
        {#if showFooterBar}
            <MiniPlayer {item} playerClick={handleOpenFullScreenPlayer} />
        {:else}
            <ChromePlayer
                {item}
                {playbackType}
                on:launchFullScreenPlayer={handleOpenFullScreenPlayer}
                on:navigate={handleNavigate}
            />
        {/if}

        {#if $fullScreenPlayerState.isVisible}
            <FullScreenPlayer
                isFullScreenActive={$fullScreenPlayerState.isVisible}
                modalTriggerElement={fullScreenPlayerTriggerElement}
                translateFn={$i18n.t}
                on:close={handleCloseFullScreenPlayer}
            />
        {/if}
    {/if}
</div>

<style lang="scss">
    @use '@amp/web-shared-styles/app/core/globalvars' as *;

    .player-bar {
        width: 100%;
        height: $global-player-bar-height;
        flex-shrink: 0;
        position: fixed;
        bottom: 0;
        transition: opacity 250ms, transform 250ms;
        inset-inline-end: 0;

        // Stack player bar just below mobile header
        z-index: calc(var(--z-web-chrome) - 1);

        @media (--range-small-only) {
            // Clamp volume width when `popover-toggle` is visible
            &:has(.popover-toggle) {
                --chromeVolumeWidth: clamp(32px, 100%, 70px);
            }
        }

        @media (--sidebar-visible) {
            width: calc(100vw - var(--web-navigation-width));
            position: fixed;
            top: 0;
            inset-inline-start: unset;
            bottom: unset;
        }

        @media (--range-sidebar-hidden-down) {
            &.player-bar__floating-player {
                height: 61px;
                background-image: linear-gradient(
                    0deg,
                    rgba(255, 255, 255, 0.8) 0,
                    transparent 100%
                );

                @media (prefers-color-scheme: dark) {
                    background-image: linear-gradient(
                        0deg,
                        rgba(0, 0, 0, 0.8) 0,
                        transparent 100%
                    );
                }

                &::before,
                &::after {
                    content: '';
                    display: block;
                    position: fixed;
                    height: 30px;
                    width: 100%;
                    left: 0; // RTL not needed
                    z-index: calc(var(--z-default) - 2);
                }

                &::before {
                    height: 31px;
                    bottom: 30px;
                    backdrop-filter: blur(3px);
                }

                &::after {
                    height: 30px;
                    bottom: 0;
                    backdrop-filter: blur(9px);
                }

                &:global(:has(.mini-player--is-empty)) {
                    display: none;
                }
            }
        }

        @media (--range-sidebar-visible-up) and (--range-xsmall-down) {
            // Make User Menu `position: fixed` when compact player is visible, to prevent overflow issue
            --contextMenuPosition: fixed;
        }

        // Hide Player Bar for Replay Highlights
        &:global(.is-hidden) {
            transform: translateY(-100%);
            opacity: 0;
        }
    }
</style>
